package org.hs_soft.runmynesto.domain.model.home.dashboard

import androidx.compose.runtime.Composable
import org.hs_soft.runmynesto.domain.util.helper.LanguageManager
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.DayOfWeek
import kotlinx.datetime.minus
import org.hs_soft.runmynesto.domain.model.home.template.tabs.tab_general.calendar.MonthModel
import org.hs_soft.runmynesto.domain.util.helper.Strings
import org.hs_soft.runmynesto.domain.util.toLocalDate

enum class CompareChartDaysEnum {
    SUNDAY,
    MONDAY,
    TUESDAY,
    WEDNESDAY,
    THURSDAY,
    FRIDAY,
    SATURDAY,
    LAST_WEEK,
    TWO_WEEKS_AGO,
    THREE_WEEKS_AGO;

    @Composable
    fun getTitle(): String {
        val strings = Strings.current()
        return when (this) {
            SUNDAY -> strings.sunday
            MONDAY -> strings.monday
            TUESDAY -> strings.tuesday
            WEDNESDAY -> strings.wednesday
            THURSDAY -> strings.thursday
            FRIDAY -> strings.friday
            SATURDAY -> strings.saturday
            LAST_WEEK -> strings.lastWeek
            TWO_WEEKS_AGO -> strings.twoWeeksAgo
            THREE_WEEKS_AGO -> strings.threeWeeksAgo
        }
    }

    val title: String
        get() = LanguageManager.getCurrentStrings().let { strings ->
            when (this) {
                SUNDAY -> strings.sundayFull
                MONDAY -> strings.mondayFull
                TUESDAY -> strings.tuesdayFull
                WEDNESDAY -> strings.wednesdayFull
                THURSDAY -> strings.thursdayFull
                FRIDAY -> strings.fridayFull
                SATURDAY -> strings.saturdayFull
                LAST_WEEK -> strings.lastWeek
                TWO_WEEKS_AGO -> strings.twoWeeksAgo
                THREE_WEEKS_AGO -> strings.threeWeeksAgo
            }
        }

    companion object {
        fun create(selectedDate: MonthModel): List<CompareChartDay> {
            return values().map { dayEnum ->
                val dayOfWeek = when (dayEnum) {
                    SUNDAY -> DayOfWeek.SUNDAY
                    MONDAY -> DayOfWeek.MONDAY
                    TUESDAY -> DayOfWeek.TUESDAY
                    WEDNESDAY -> DayOfWeek.WEDNESDAY
                    THURSDAY -> DayOfWeek.THURSDAY
                    FRIDAY -> DayOfWeek.FRIDAY
                    SATURDAY -> DayOfWeek.SATURDAY
                    else -> null
                }

                val date = if (dayOfWeek != null) {
                    getDateForDay(dayOfWeek, selectedDate)
                } else {
                    when (dayEnum) {
                        LAST_WEEK -> getDateWeeksAgo(1, selectedDate)
                        TWO_WEEKS_AGO -> getDateWeeksAgo(2, selectedDate)
                        THREE_WEEKS_AGO -> getDateWeeksAgo(3, selectedDate)
                        else -> MonthModel()
                    }
                }

                CompareChartDay(
                    dayEnum = dayEnum,
                    title = dayEnum.title, // Using non-composable title here
                    date = date
                )
            }
        }
    }
}

data class CompareChartDay(
    val dayEnum: CompareChartDaysEnum,
    val title: String,
    val date: MonthModel
) {
    @Composable
    fun getLocalizedTitle(): String {
        return dayEnum.getTitle()
    }
}

private fun getDateForDay(dayOfWeek: DayOfWeek, selectedDate: MonthModel): MonthModel {
    val selectedLocalDate = selectedDate.toLocalDate()
    val daysToSubtract = (selectedLocalDate.dayOfWeek.ordinal - dayOfWeek.ordinal + 7) % 7
    val targetDate = selectedLocalDate.minus(daysToSubtract.toLong(), DateTimeUnit.DAY)
    return MonthModel(
        dayNumber = targetDate.dayOfMonth,
        monthNumber = targetDate.monthNumber,
        yearNumber = targetDate.year,
        monthName = targetDate.month.name
    )
}

private fun getDateWeeksAgo(weeksAgo: Int, selectedDate: MonthModel): MonthModel {
    val selectedLocalDate = selectedDate.toLocalDate()
    val targetDate = selectedLocalDate.minus(weeksAgo.toLong(), DateTimeUnit.WEEK)
    return MonthModel(
        dayNumber = targetDate.dayOfMonth,
        monthNumber = targetDate.monthNumber,
        yearNumber = targetDate.year
    )
}

private fun getTitleForDay(dayOfWeek: DayOfWeek, selectedDate: MonthModel): String {
    val selectedLocalDate = selectedDate.toLocalDate()
    val strings = LanguageManager.getCurrentStrings()
    return if (selectedLocalDate.dayOfWeek == dayOfWeek) {
        "${dayOfWeek.name.lowercase().replaceFirstChar { it.uppercase() }} (${strings.today})"
    } else {
        dayOfWeek.name.lowercase().replaceFirstChar { it.uppercase() }
    }
}